import { useState, useCallback, useMemo } from 'react';
import debounce from 'lodash.debounce';
import RestoApiService from '../utils/RestoApiService';
import { MapLocation } from '../utils/SuperclusterManager';

interface UseMapDataFetcherProps {
  rangeValue: number[];
  setIsFiltering: React.Dispatch<React.SetStateAction<boolean>>;
  restoApiService: RestoApiService;
  selectedMarkerId: string | null;
}

export function useMapDataFetcher({
  rangeValue,
  setIsFiltering,
  restoApiService,
  selectedMarkerId,
}: UseMapDataFetcherProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [locations, setLocations] = useState<MapLocation[]>([]);

  /**
   * Helper to merge selected location into locations array
   */
  const mergeWithSelectedLocation = useCallback(
    async (mappedLocations: MapLocation[]): Promise<MapLocation[]> => {
      if (!selectedMarkerId) {
        return mappedLocations;
      }

      // Check if selected marker is already in the locations
      let selectedLocation = mappedLocations.find(loc => loc.id === selectedMarkerId) || null;

      // If not found, fetch it directly
      if (!selectedLocation) {
        try {
          const result = await restoApiService.fetchRestoById(selectedMarkerId);
          if (result.success && result.data) {
            const { latitude, longitude, id } = result.data;
            if (latitude && longitude) {
              selectedLocation = {
                lat: latitude,
                lng: longitude,
                id: id,
                entry: result.data,
              };
            }
          }
        } catch (fetchError) {
          console.error("Error fetching selected restaurant:", fetchError);
        }
      }

      // Merge selected location if found and not already in array
      if (selectedLocation && !mappedLocations.some(loc => loc.id === selectedLocation?.id)) {
        return [...mappedLocations, selectedLocation];
      }
      return mappedLocations;
    },
    [selectedMarkerId, restoApiService]
  );

  /**
   * Immediately render locations from cache (NOT debounced).
   * Called on every bounds change to show cached data instantly.
   */
  const renderFromCache = useCallback(
    (bounds: google.maps.LatLngBounds | null): void => {
      if (!bounds) return;

      const selectedForks: number[] = rangeValue;
      const cachedLocations = restoApiService.getCachedLocationsInBounds(bounds, selectedForks);

      if (cachedLocations.length > 0) {
        setLocations(cachedLocations);
      }
    },
    [rangeValue, restoApiService]
  );

  /**
   * Fetch fresh data from API (should be called via debounce).
   * Updates locations with API data and animations will show the diff.
   */
  const fetchMapEntries = useCallback(
    async (bounds: google.maps.LatLngBounds | null): Promise<void> => {
      if (!bounds) {
        console.warn("fetchMapEntries called with null bounds");
        return;
      }

      setIsFiltering(true);
      setIsLoading(true);

      try {
        const selectedForks: number[] = rangeValue;
        const mappedLocations = await restoApiService.fetchMapEntries(bounds, selectedForks);

        // Merge with selected location
        const newLocations = await mergeWithSelectedLocation(mappedLocations);

        // Update locations state - SuperclusterManager handles incremental updates with animation
        setLocations(newLocations);

        setIsFiltering(false);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching map entries:", error);
        setIsFiltering(false);
        setIsLoading(false);
      }
    },
    [rangeValue, setIsFiltering, restoApiService, mergeWithSelectedLocation],
  );

  const debouncedFetchMapEntries = useMemo(
    () =>
      debounce((bounds: google.maps.LatLngBounds | null) => {
        fetchMapEntries(bounds);
      }, 300),
    [fetchMapEntries],
  );

  return {
    isLoading,
    locations,
    setLocations,
    renderFromCache,
    fetchMapEntries,
    debouncedFetchMapEntries,
  };
}
